import { Controller } from "@hotwired/stimulus"
import { getUserLocale, parseInputNumber } from "../../utils/number-format.util"
import { formatPercent, serverNumberFormatFor } from "../../utils/server-format.util"

// Rates are compared and printed to the hundredth: statutory rates carry
// decimals (FR 5.5 / 2.1, IE 13.5, FI 25.5), so rounding to a whole number
// warned about every one of them as "6%" or "26%".
const toHundredths = (rate) => Math.round(rate * 100) / 100
const RATE_PRECISION = { minimumFractionDigits: 0, maximumFractionDigits: 2 }

// Connects to data-controller="documents--vat-rate-warning"
export default class extends Controller {
  static targets = ["message"]

  static values = {
    allowedRates: Array,
    country: String
  }

  connect() {
    this.locale = getUserLocale()
    this.handleFocusOut = (event) => {
      if (event.target?.matches("[data-documents--item-target='taxRate']")) {
        this.#checkRates()
      }
    }

    this.handleVatRateChanged = () => this.#checkRates()

    const itemsContainer = document.getElementById("document_items_tr")
    if (itemsContainer) {
      itemsContainer.addEventListener("focusout", this.handleFocusOut)
      itemsContainer.addEventListener("documents--item:vat-rate-changed", this.handleVatRateChanged)
      this.observer = new MutationObserver(() => this.#checkRates())
      this.observer.observe(itemsContainer, { childList: true })
    }

    requestAnimationFrame(() => this.#checkRates())
  }

  disconnect() {
    const itemsContainer = document.getElementById("document_items_tr")
    itemsContainer?.removeEventListener("focusout", this.handleFocusOut)
    itemsContainer?.removeEventListener("documents--item:vat-rate-changed", this.handleVatRateChanged)
    this.observer?.disconnect()
  }

  #checkRates() {
    const vatInputs = document.querySelectorAll("[data-documents--item-target='taxRate']")
    const allowedRates = this.allowedRatesValue.map((r) => toHundredths(Number(r)))
    const invalidRates = new Set()

    for (const input of vatInputs) {
      const row = input.closest("tr.invoice-item")
      if (row) {
        const destroyInput = row.querySelector("input[type='hidden'][name*='_destroy']")
        if (destroyInput && destroyInput.value === "1") continue
      }

      const rate = parseInputNumber(input, this.locale)
      if (!Number.isFinite(rate)) continue
      if (rate === 0) continue

      const rounded = toHundredths(rate)
      if (!allowedRates.includes(rounded)) {
        invalidRates.add(rounded)
      }
    }

    if (invalidRates.size > 0) {
      const numberFormat = serverNumberFormatFor(this.element) || {}
      const percent = (r) => formatPercent(r, numberFormat, RATE_PRECISION)
      const ratesText = [...invalidRates].map(percent).join(", ")
      const allowedText = allowedRates
        .filter((r) => r > 0)
        .map(percent)
        .join(", ")
      const template = this.messageTarget.dataset.template
      if (!template) return

      this.messageTarget.textContent = template
        .replace("%{rates}", ratesText)
        .replace("%{allowed}", allowedText)
        .replace("%{country}", this.countryValue)
      this.element.classList.remove("d-none")
    } else {
      this.element.classList.add("d-none")
    }
  }
}
